import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Skeleton } from '@/components/ui';
import type { OrgAdminRow, Organization, OrgTenant, ProvisioningStatus } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';

interface BlockingItem {
    key: string;
    text: string;
}

// EAA-1845: takes `t` explicitly since this is a plain helper called from the
// component body, not a component itself — it cannot call the
// `useTranslation` hook on its own.
function blockingItems(coverage: ProvisioningStatus, t: TFunction): BlockingItem[] {
    const items: BlockingItem[] = [];
    if (coverage.assignments.error > 0) {
        items.push({
            key: 'assignment-error',
            text: t('admin.organizations.overview.assignmentErrorText', {
                count: coverage.assignments.error,
            }),
        });
    }
    if (coverage.orphan_levels > 0) {
        items.push({
            key: 'orphan-levels',
            text: t('admin.organizations.overview.orphanLevelsText', {
                count: coverage.orphan_levels,
            }),
        });
    }
    return items;
}

interface OrgOverviewTabProps {
    org: Organization;
    tenants: OrgTenant[];
    tenantsLoading: boolean;
    admins: OrgAdminRow[];
    adminsLoading: boolean;
    coverage: ProvisioningStatus | undefined;
    coverageLoading: boolean;
    isSuperAdmin: boolean;
    canManage: boolean;
    onAddTenant: () => void;
    onGoToTenants: () => void;
    onGoToAdmins: () => void;
    onGoToBranding: () => void;
    onViewHierarchy: () => void;
    onToggleOrgActive: () => void;
    toggling: boolean;
}

/**
 * EAA-1815: `/organizations/:id` — "Tổng quan" tab, frame 22's main body
 * (lines 155-599). "Leads with what's broken": the blocking-issues card is
 * built from `GET /organizations/{id}/provisioning-status` — the same real
 * endpoint `HierarchyPage`'s coverage card reads — and is absent entirely
 * when `coverage.status !== 'attention'`, never rendered empty/green.
 *
 * The Cơ sở preview's "Cấp cha" column always reads the organization's own
 * name: `OrgTenant` carries no `parent_tenant_id` (confirmed against
 * `src/api/orgAdmin.ts`), so tenant-under-tenant nesting from the design
 * frame (a tenant like "Khối 1–5" parented under another tenant) has no real
 * data behind it today — only the flat org→tenant relationship the D16 model
 * actually has. See the ticket report for this deviation.
 */
export function OrgOverviewTab({
    org,
    tenants,
    tenantsLoading,
    admins,
    adminsLoading,
    coverage,
    coverageLoading,
    isSuperAdmin,
    canManage,
    onAddTenant,
    onGoToTenants,
    onGoToAdmins,
    onGoToBranding,
    onViewHierarchy,
    onToggleOrgActive,
    toggling,
}: OrgOverviewTabProps) {
    const { t } = useTranslation();
    const blocking =
        coverage && coverage.status === 'attention' ? blockingItems(coverage, t) : [];
    const brandColor = org.branding?.primary_color;
    const accountsActive = coverage?.assignments.active;

    return (
        <div className="portal-org-detail-body">
            <div className="portal-org-detail-main">
                {coverageLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.overview.loadingCoverageSrOnly')}
                        </span>
                        <Skeleton height="4rem" />
                    </div>
                )}

                {blocking.length > 0 && (
                    <div className="portal-org-blocking-card" role="alert">
                        <span className="portal-org-blocking-head">
                            <span className="portal-org-blocking-icon">!</span>
                            <span className="portal-org-blocking-title">
                                {t('admin.organizations.overview.blockingTitle', {
                                    count: blocking.length,
                                })}
                            </span>
                        </span>
                        {blocking.map((item) => (
                            <span className="portal-org-blocking-item" key={item.key}>
                                <span className="portal-org-blocking-item-text">{item.text}</span>
                                <button
                                    type="button"
                                    className="portal-org-blocking-item-action"
                                    onClick={onViewHierarchy}
                                >
                                    {t('admin.organizations.overview.blockingItemAction')}
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div className="portal-org-tenanttable-card">
                    <span className="portal-org-tenanttable-title">
                        {t('admin.organizations.tenantsLabel')}
                    </span>
                    {tenantsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="6rem" />
                        </div>
                    )}
                    {!tenantsLoading && tenants.length === 0 && (
                        <p className="portal-org-notenant-callout">
                            {t('admin.organizations.overview.noTenantCallout')}
                            {canManage && (
                                <button
                                    type="button"
                                    className="portal-org-blocking-item-action"
                                    onClick={onAddTenant}
                                >
                                    {t('admin.organizations.createTenantAction')}
                                </button>
                            )}
                        </p>
                    )}
                    {!tenantsLoading && tenants.length > 0 && (
                        <table className="portal-org-tenant-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.organizations.columnName')}</th>
                                    <th>{t('admin.organizations.columnParentLevel')}</th>
                                    <th>{t('admin.organizations.columnStatus')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.slice(0, 6).map((tenant) => (
                                    <tr key={tenant.id}>
                                        <td className="portal-org-tenant-name">
                                            {tenant.name ?? tenant.tenant_id}
                                        </td>
                                        <td className="portal-org-tenant-parent">{org.name}</td>
                                        <td>
                                            <span
                                                className={
                                                    'portal-console-badge' +
                                                    (tenant.tenant_is_active ? ' is-on' : '')
                                                }
                                            >
                                                {tenant.tenant_is_active
                                                    ? t('admin.organizations.status.active')
                                                    : t('admin.organizations.statusTabs.inactive')}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>
                                            ⋯
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {tenants.length > 6 && (
                        <button
                            type="button"
                            className="portal-org-aside-link"
                            onClick={onGoToTenants}
                        >
                            {t('admin.organizations.overview.viewAllTenants', {
                                count: tenants.length,
                            })}
                        </button>
                    )}
                </div>
            </div>

            <div className="portal-org-detail-aside">
                <div className="portal-org-aside-card">
                    <span className="portal-org-aside-card-title">
                        {t('admin.organizations.detail.tabs.admins')}
                    </span>
                    {adminsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="2rem" />
                        </div>
                    )}
                    {!adminsLoading &&
                        admins.slice(0, 3).map((admin) => (
                            <span className="portal-org-admin-row" key={admin.id}>
                                <OrgAvatar
                                    name={admin.user_email || admin.user_sub}
                                    size={28}
                                    radius={999}
                                    tone="accent"
                                    fontSize={11}
                                />
                                <span className="portal-org-admin-row-identity">
                                    <span className="portal-org-admin-row-name">
                                        {admin.user_email || admin.user_sub}
                                    </span>
                                </span>
                            </span>
                        ))}
                    {!adminsLoading && admins.length === 0 && (
                        <span className="portal-org-aside-hint">
                            {t('admin.organizations.overview.noAdminsHint')}
                        </span>
                    )}
                    {isSuperAdmin && (
                        <button type="button" className="portal-org-aside-link" onClick={onGoToAdmins}>
                            {t('admin.organizations.overview.addAdminLink')}
                        </button>
                    )}
                </div>

                <div className="portal-org-aside-card">
                    <span className="portal-org-aside-card-title">
                        {t('admin.organizations.detail.tabs.branding')}
                    </span>
                    <span className="portal-org-brand-row">
                        <span
                            className="portal-org-brand-swatch"
                            style={{ background: brandColor || 'var(--color-primary)' }}
                            aria-hidden="true"
                        />
                        <code className="portal-org-brand-code">
                            {brandColor
                                ? brandColor.toUpperCase()
                                : t('admin.organizations.defaultBrandLabel')}
                        </code>
                        {canManage && (
                            <button
                                type="button"
                                className="portal-org-aside-link"
                                onClick={onGoToBranding}
                            >
                                {t('common.edit')}
                            </button>
                        )}
                    </span>
                    <span className="portal-org-aside-hint">
                        {t('admin.organizations.overview.brandingHint')}
                    </span>
                </div>

                {isSuperAdmin && (
                    <div className="portal-org-aside-card">
                        <span className="portal-org-aside-card-title">
                            {t('admin.organizations.overview.dangerCardTitle')}
                        </span>
                        <span className="portal-org-aside-hint">
                            {org.is_active
                                ? t('admin.organizations.overview.dangerHintActive', {
                                      value:
                                          accountsActive === undefined
                                              ? t('admin.organizations.overview.allAccountsWord')
                                              : accountsActive,
                                  })
                                : t('admin.organizations.overview.dangerHintInactive')}
                        </span>
                        <button
                            type="button"
                            className={org.is_active ? 'portal-org-danger-btn' : 'portal-btn'}
                            onClick={onToggleOrgActive}
                            disabled={toggling}
                        >
                            {toggling
                                ? t('admin.organizations.processingLabel')
                                : org.is_active
                                  ? t('admin.organizations.deactivateAction')
                                  : t('admin.organizations.reactivateAction')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OrgOverviewTab;
